import { ApiFile } from "@/types/common.types.ts";
import { ApiDocumentPreview, ApiDocumentPreviewStatus } from "@/types/document.types.ts";
import { useQuery } from "@tanstack/react-query";

const PREVIEW_POLL_INTERVAL_MS = 4000;

export const NON_PREVIEWABLE_DOCUMENT_EXTENSIONS = new Set([
  "doc",
  "rtf",
  "odt",
  "xls",
  "xlsx",
  "ods",
  "ppt",
  "pptx",
  "odp",
]);

function isNonPreviewableDocument(file: ApiFile): boolean {
  const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
  return NON_PREVIEWABLE_DOCUMENT_EXTENSIONS.has(extension);
}

export interface UseDocumentPreviewStateParams {
  file: ApiFile;
  preview?: ApiDocumentPreview;
  refetchPreview?: () => Promise<ApiDocumentPreview | undefined>;
}

interface UseDocumentPreviewStateResult {
  sourceFile: ApiFile | null;
  hasPreviewContract: boolean;
  isPreparing: boolean;
  isReady: boolean;
  isPreviewFailed: boolean;
}

export function useDocumentPreviewState({
  file,
  preview,
  refetchPreview,
}: UseDocumentPreviewStateParams): UseDocumentPreviewStateResult {
  const isForcedRawFile = isNonPreviewableDocument(file);
  const canPoll = !!refetchPreview;

  const previewPollQuery = useQuery({
    queryKey: ["file-preview", file.uuid],
    queryFn: async () => {
      return (await refetchPreview?.()) ?? null;
    },
    enabled: !isForcedRawFile && canPoll && preview?.status === ApiDocumentPreviewStatus.pending,
    refetchInterval: (query) => {
      const status = query.state.data?.status ?? preview?.status;
      return status === ApiDocumentPreviewStatus.pending ? PREVIEW_POLL_INTERVAL_MS : false;
    },
  });

  const livePreview =
    preview?.status === ApiDocumentPreviewStatus.pending
      ? (previewPollQuery.data ?? preview)
      : preview;
  const hasPreviewContract = preview !== undefined && !isForcedRawFile;
  const status = livePreview?.status;

  return {
    sourceFile: hasPreviewContract ? (livePreview?.file ?? null) : file,
    hasPreviewContract,
    isPreparing: hasPreviewContract && status === ApiDocumentPreviewStatus.pending,
    isReady: hasPreviewContract && status === ApiDocumentPreviewStatus.ready,
    isPreviewFailed: hasPreviewContract && status === ApiDocumentPreviewStatus.failed,
  };
}
